import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { productService } from '../../services/productService';

const OpportunitiesTab = () => {
  const [loading, setLoading] = useState(false);
  const [opportunities, setOpportunities] = useState([]);

  useEffect(() => {
    loadOpportunities();
  }, []);

  const loadOpportunities = async () => {
    try {
      setLoading(true);
      const { products } = await productService?.getAll();
      
      // Analyze catalog for opportunities
      const foundOpportunities = analyzeOpportunities(products);
      setOpportunities(foundOpportunities);
    } catch (error) {
      console.error('Error loading opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeOpportunities = (products) => {
    const opportunities = [];

    // 1. Low stock but high demand in toys
    const toyCategory = products?.filter(p => 
      p?.category?.toLowerCase()?.includes('brinquedo') || 
      p?.category?.toLowerCase()?.includes('toy')
    );
    const lowStockToys = toyCategory?.filter(p => p?.stock > 0 && p?.stock < 10)?.length;
    if (lowStockToys > 0) {
      opportunities?.push({
        id: 'low-stock-toys',
        icon: 'AlertTriangle',
        title: `⚠️ ${lowStockToys} produtos com estoque baixo mas alta procura em brinquedos`,
        description: 'Reabasteça antes de perder vendas',
        severity: 'high',
        affectedCount: lowStockToys,
        actionable: true
      });
    }

    // 2. Products without detailed description
    const noDescription = products?.filter(p => !p?.description || p?.description?.length < 50)?.length;
    if (noDescription > 0) {
      opportunities?.push({
        id: 'no-description',
        icon: 'FileText',
        title: `⚠️ ${noDescription} produtos sem descrição detalhada (prejudica SEO)`,
        description: 'Adicione descrições completas para melhorar ranqueamento',
        severity: 'medium',
        affectedCount: noDescription,
        actionable: true
      });
    }

    // 3. Category pricing above competition
    const schoolCategory = products?.filter(p => 
      p?.category?.toLowerCase()?.includes('escolar') || 
      p?.category?.toLowerCase()?.includes('school')
    );
    if (schoolCategory?.length > 0) {
      const avgSchoolPrice = schoolCategory?.reduce((sum, p) => sum + (p?.price || 0), 0) / schoolCategory?.length;
      const highPriced = schoolCategory?.filter(p => p?.price > avgSchoolPrice * 1.2)?.length;
      if (highPriced > 0) {
        opportunities?.push({
          id: 'high-pricing-school',
          icon: 'TrendingUp',
          title: `⚠️ Categoria 'Escolar' com preços 20% acima da concorrência`,
          description: `${highPriced} produtos podem estar perdendo vendas`,
          severity: 'high',
          affectedCount: highPriced,
          actionable: true
        });
      }
    }

    // 4. Products with only 1 image
    const fewImages = products?.filter(p => !p?.images || p?.images?.length < 2)?.length;
    if (fewImages > 0) {
      opportunities?.push({
        id: 'few-images',
        icon: 'Image',
        title: `⚠️ ${fewImages} produtos com apenas 1 imagem (adicione mais fotos)`,
        description: 'Produtos com mais imagens convertem 40% melhor',
        severity: 'medium',
        affectedCount: fewImages,
        actionable: true
      });
    }

    // 5. Products not published on any marketplace
    const notPublished = products?.filter(p => 
      !p?.mercadolivreReady && 
      !p?.shopeeReady && 
      !p?.amazonReady
    )?.length;
    if (notPublished > 0) {
      opportunities?.push({
        id: 'not-published',
        icon: 'ShoppingBag',
        title: `⚠️ ${notPublished} produtos não publicados em nenhum marketplace`,
        description: 'Configure para começar a vender',
        severity: 'high',
        affectedCount: notPublished,
        actionable: true
      });
    }

    return opportunities;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      high: 'border-l-warning bg-warning/5',
      medium: 'border-l-yellow-500 bg-yellow-50',
      low: 'border-l-info bg-info/5'
    };
    return colors?.[severity] || colors?.medium;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {opportunities?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
          <p className="text-foreground font-semibold mb-2">✨ Nenhuma Oportunidade Perdida!</p>
          <p className="text-muted-foreground text-sm">Seu catálogo está otimizado.</p>
        </div>
      ) : (
        opportunities?.map((opportunity) => (
          <div 
            key={opportunity?.id} 
            className={`bg-card border-l-4 ${getSeverityColor(opportunity?.severity)} rounded-lg p-4 hover:shadow-brand-md transition-smooth`}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name={opportunity?.icon} size={20} className="text-warning" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">{opportunity?.title}</h3>
                <p className="text-sm text-muted-foreground">{opportunity?.description}</p>
              </div>
            </div>
            
            {opportunity?.affectedCount && (
              <div className="bg-muted/50 rounded-md px-3 py-2 mb-3">
                <p className="text-xs text-muted-foreground">
                  📊 <strong>{opportunity?.affectedCount} produtos</strong> afetados
                </p>
              </div>
            )}
            
            {opportunity?.actionable && (
              <Button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('statusUpdate', {
                    detail: {
                      type: 'info',
                      message: 'Redirecionando para catálogo...',
                      progress: 100
                    }
                  }));
                  // Navigate to product catalog with filter
                  window.location.href = '/product-catalog-manager';
                }}
                variant="outline"
                className="w-full"
              >
                <Icon name="ArrowRight" size={16} className="mr-2" />
                Ver Produtos Afetados
              </Button>
            )}
          </div>
        ))
      )}
      
      <div className="bg-gradient-to-r from-warning/10 to-yellow-500/10 border border-warning/20 rounded-lg p-4">
        <p className="text-sm text-muted-foreground">
          💡 <strong>Dica:</strong> Resolver essas oportunidades pode aumentar suas vendas em até 45%!
        </p>
      </div>
    </div>
  );
};

export default OpportunitiesTab;